/**
 * NBA Sport Configuration
 *
 * Positions, prop categories, roster slots, teams, and brand identity
 * for the NBA sport within FantasyPRIME.
 */

import type { SportConfig } from './types';

export const nbaConfig: SportConfig = {
  sport: 'NBA',
  label: 'NBA Basketball',
  shortLabel: 'NBA',

  oddsApiSportKey: 'basketball_nba',

  drawLabel: 'Tie',
  espnPath: { sport: 'basketball', league: 'nba' },

  positions: [
    { id: 'PG', label: 'Point Guard', abbreviation: 'PG' },
    { id: 'SG', label: 'Shooting Guard', abbreviation: 'SG' },
    { id: 'SF', label: 'Small Forward', abbreviation: 'SF' },
    { id: 'PF', label: 'Power Forward', abbreviation: 'PF' },
    { id: 'C', label: 'Center', abbreviation: 'C' },
  ],

  propCategories: [
    // 'Points', not 'Goals' — this label is written into prop descriptions
    // (line-refresh builds "<player> — <label> Over <line>") and the
    // settlement parser matches on it. 'Goals' was a leftover from soccer and
    // would have made these props unsettleable if NBA ever went through the
    // line-refresh path.
    { id: 'POINTS', label: 'Points', oddsApiMarket: 'player_points' },
    { id: 'REBOUNDS', label: 'Rebounds', oddsApiMarket: 'player_rebounds' },
    { id: 'ASSISTS', label: 'Assists', oddsApiMarket: 'player_assists' },
    { id: 'STEALS', label: 'Steals', oddsApiMarket: 'player_steals' },
    { id: 'BLOCKS', label: 'Blocks', oddsApiMarket: 'player_blocks' },
    { id: 'THREE_POINTERS_MADE', label: '3-Pointers Made', oddsApiMarket: 'player_threes' },
    { id: 'TURNOVERS', label: 'Turnovers', oddsApiMarket: 'player_turnovers' },
    { id: 'POINTS_REBOUNDS_ASSISTS', label: 'Pts+Reb+Ast', oddsApiMarket: 'player_points_rebounds_assists' },
    { id: 'POINTS_REBOUNDS', label: 'Pts+Reb', oddsApiMarket: 'player_points_rebounds' },
    { id: 'POINTS_ASSISTS', label: 'Pts+Ast', oddsApiMarket: 'player_points_assists' },
    { id: 'REBOUNDS_ASSISTS', label: 'Reb+Ast', oddsApiMarket: 'player_rebounds_assists' },
    { id: 'DOUBLE_DOUBLE', label: 'Double-Double', oddsApiMarket: 'player_double_double' },
  ],

  defaultRosterSlots: [
    { slot: 'PG', eligiblePositions: ['PG'], label: 'Point Guard' },
    { slot: 'SG', eligiblePositions: ['SG'], label: 'Shooting Guard' },
    { slot: 'SF', eligiblePositions: ['SF'], label: 'Small Forward' },
    { slot: 'PF', eligiblePositions: ['PF'], label: 'Power Forward' },
    { slot: 'C', eligiblePositions: ['C'], label: 'Center' },
    { slot: 'G', eligiblePositions: ['PG', 'SG'], label: 'Guard' },
    { slot: 'F', eligiblePositions: ['SF', 'PF'], label: 'Forward' },
    { slot: 'UTIL', eligiblePositions: ['PG', 'SG', 'SF', 'PF', 'C'], label: 'Utility' },
  ],

  defaultRosterSize: 8,
  defaultMaxProps: 5,

  identity: {
    color: '#B87333',
    metal: 'Copper',
    icon: 'CircleDot', // basketball
  },

  teams: {
    ATL: 'Atlanta Hawks',
    BOS: 'Boston Celtics',
    BKN: 'Brooklyn Nets',
    CHA: 'Charlotte Hornets',
    CHI: 'Chicago Bulls',
    CLE: 'Cleveland Cavaliers',
    DAL: 'Dallas Mavericks',
    DEN: 'Denver Nuggets',
    DET: 'Detroit Pistons',
    GSW: 'Golden State Warriors',
    HOU: 'Houston Rockets',
    IND: 'Indiana Pacers',
    LAC: 'Los Angeles Clippers',
    LAL: 'Los Angeles Lakers',
    MEM: 'Memphis Grizzlies',
    MIA: 'Miami Heat',
    MIL: 'Milwaukee Bucks',
    MIN: 'Minnesota Timberwolves',
    NOP: 'New Orleans Pelicans',
    NYK: 'New York Knicks',
    OKC: 'Oklahoma City Thunder',
    ORL: 'Orlando Magic',
    PHI: 'Philadelphia 76ers',
    PHX: 'Phoenix Suns',
    POR: 'Portland Trail Blazers',
    SAC: 'Sacramento Kings',
    SAS: 'San Antonio Spurs',
    TOR: 'Toronto Raptors',
    UTA: 'Utah Jazz',
    WAS: 'Washington Wizards',
  },
};
